import type { SagaIterator } from 'redux-saga';
import { all, call, put, select } from 'redux-saga/effects';
import groupBy from 'lodash/groupBy';
import { selectors, withOffset } from '../../Stream';
import { requestPuppy } from '@wix/da-http-client';
import { getCurrentUser } from '../../publicSession/selectors';
import { GallectionStreamParams, MediaSourceType, MediaPopupMode } from '../types';
import { GallectionType } from '../../types/gallection';
import { fetchMoreGallectionsDone } from '../actions';
import { getGrusername, getMode } from '../selectors';
import { getGallectionStreamId, getGallectionType } from '../helpers';
import { getStreamInitAction } from './initGallectionStream';
import type {
  PapiDaSharedGallectionFolders,
  PapiGallection,
  PapiRequestDaSharedGallectionFolders,
} from '@wix/da-papi-types';
import { ALL_FOLDER_ID, ALL_FOLDER_NAME } from '@wix/da-url';
import { FetchAction, FetchType } from '../../Stream/base/types';
import { normalizeGallections } from '../../redux/normalizr/helpers';

export function fakeResponse({ params }): any {
  if (process.env.NODE_ENV !== 'production') {
    let gallections: PapiGallection[];
    const putFeaturedFolderFirst = (a, b) => (a.name === 'Featured' ? -1 : 1);
    if (params.type === MediaSourceType.GALLERY) {
      const { galleries, galleriesFolderScraps } = require('@wix/da-sample-data'); // eslint-disable-line global-require
      gallections = galleries.map(gallection => ({
        ...gallection,
        permissions: {
          ...gallection.permissions,
          createFolder: true,
          editFolder: false,
          deleteFolder: false,
        },
      }));
      gallections = [...gallections, galleriesFolderScraps];
      gallections = gallections.map((gallery, index) =>
        // make every 5th gallery a subfolder of the second one (after Featured which can't have subfolders)
        index % 5 === 2 // starting from 2 to leave Featured, Scraps folders alone
          ? { ...gallery, parentId: gallections[1].folderId }
          : gallery
      );
      const fakeAllFolder: PapiGallection = {
        ...gallections[2],
        parentId: null,
        folderId: ALL_FOLDER_ID,
        name: ALL_FOLDER_NAME,
      };
      const paidGallection: PapiGallection = {
        ...gallections[2],
        parentId: null,
        premiumFolderData: {
          type: 'paid',
          hasAccess: true,
          pointsPrice: 800,
          dollarPrice: 10,
          numSubscribers: 3,
        },
      };
      const watchersGallection: PapiGallection = {
        ...gallections[3],
        parentId: null,
        premiumFolderData: {
          type: 'watchers',
          hasAccess: true,
          pointsPrice: 800,
          dollarPrice: 10,
          numSubscribers: 3,
        },
      };
      gallections = [fakeAllFolder, paidGallection, watchersGallection, ...gallections.slice(4)];
    } else if (params.type === MediaSourceType.STASH) {
      const { stashGallections } = require('@wix/da-sample-data'); // eslint-disable-line global-require
      gallections = stashGallections;
    } else {
      const { collections } = require('@wix/da-sample-data'); // eslint-disable-line global-require
      gallections = collections.sort(putFeaturedFolderFirst);
    }
    if (window.doError) {
      return {
        error: 'invalid_request',
        error_description: 'Not logged in.',
        status: 'error',
      };
    }

    return {
      results: gallections,
      hasMore: true,
    };
  }
}

function* fetch(url: string, params: object) {
  const response = yield call(
    requestPuppy,
    {
      method: 'get',
      url,
      params,
    },
    fakeResponse
  );
  return response;
}

function getStreamFetchSuccessAction(
  mediaSource: MediaSourceType,
  folderId: number,
  gallectionFolders: PapiGallection[]
) {
  const streamId = getGallectionStreamId(mediaSource, folderId);
  const { result, entities } = normalizeGallections(gallectionFolders);

  return withOffset.actions.fetchSuccessDeduped({
    streamId,
    hasMore: false, // we fetch all gallections at once
    items: result,
    entities,
  });
}

function* handleGallectionStreamFetch(action: FetchAction): SagaIterator {
  const { streamId } = action;

  const {
    offset,
    limit,
    mediaSource,
    folderId,
    grusername,
    currentUser,
    mode,
  }: {
    offset: ReturnType<typeof withOffset.selectors.getNextOffset>;
    limit: ReturnType<typeof selectors.getItemsPerFetch>;
    mediaSource: Partial<GallectionStreamParams>['mediaSource'];
    folderId: number | undefined;
    grusername: string | undefined;
    currentUser: ReturnType<typeof getCurrentUser>;
    mode: MediaPopupMode;
  } = yield select(state => ({
    offset: withOffset.selectors.getNextOffset(state, streamId),
    limit: selectors.getItemsPerFetch(state, streamId),
    mediaSource: selectors.getStreamParam(state, streamId, 'mediaSource'),
    folderId: selectors.getStreamParam(state, streamId, 'folderId'),
    grusername: getGrusername(state),
    currentUser: getCurrentUser(state),
    mode: getMode(state),
  }));

  if (!mediaSource) {
    return;
  }

  const gallectionType = getGallectionType(mediaSource);

  // Groups do not have sta.sh, so we always have to interface with the user's sta.sh
  const username = gallectionType === GallectionType.STASH ? currentUser.username : grusername;

  const params: PapiRequestDaSharedGallectionFolders = {
    offset,
    limit,
    folderid: folderId,
    type: gallectionType ?? 'collection',
    with_all_folder: mediaSource !== MediaSourceType.STASH,
    with_permissions: mode === MediaPopupMode.MANAGER,
    ...(username && { username }),
  };

  const response: PapiDaSharedGallectionFolders | false = yield call(
    fetch,
    'gallection/folders',
    params
  );

  // TODO: proper error handling
  if (!response) {
    yield put(fetchMoreGallectionsDone(false, mediaSource));
    return;
  }

  const { results } = response;

  if (!results) {
    // TODO: request does not return error message atm
    yield put(
      withOffset.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: 'Failed to fetch',
      })
    );
    yield put(fetchMoreGallectionsDone(false, mediaSource));
    return;
  }

  const foldersByParentId = groupBy(results || [], 'parentId');
  const nonZeroParentIds = Object.keys(foldersByParentId).filter(parentId => +parentId);
  if (nonZeroParentIds.length) {
    // if we have folders with subfolders, need to init streams for them
    yield all(
      nonZeroParentIds.map(parentId => put(getStreamInitAction(mediaSource, Number(parentId))))
    );
  }
  yield all(
    Object.keys(foldersByParentId).map(parentId => {
      const folders = foldersByParentId[parentId];
      return put(getStreamFetchSuccessAction(mediaSource, +parentId, folders));
    })
  );
  yield put(fetchMoreGallectionsDone(true, mediaSource));
}

export default handleGallectionStreamFetch;
